import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { collection, deleteDoc, doc, onSnapshot, serverTimestamp, setDoc } from "firebase/firestore";
import { ArrowBackIcon } from "../../util/Icons";
import SubMenuWrapper, { SubMenuItem } from "../../util/SubMenu";
import { db } from "../../../firebase";
import { useUserContext } from "../../../context/UserProvider";
import ExerciseOverlay from "./ExerciseOverlay";

/**
 * WeekPage - Displays week details with exercise cards
 * Receives weekData from parent ProgramPage
 */
export default function WeekPage({ weekData, programData }) {
    const { programId } = useParams();
    const { user } = useUserContext();
    const [selectedExercise, setSelectedExercise] = useState(null);
    const [completedProgressKeys, setCompletedProgressKeys] = useState(() => new Set());
    const [completionLoadError, setCompletionLoadError] = useState("");
    const [completionActionError, setCompletionActionError] = useState("");
    const [savingProgressKey, setSavingProgressKey] = useState("");
    const isCompletionEnabled = programId === "strength-for-life";

    useEffect(() => {
        if (!isCompletionEnabled || !user?.uid) {
            setCompletedProgressKeys(new Set());
            setCompletionLoadError("");
            return undefined;
        }

        const completionsRef = collection(
            db,
            "user_activities",
            user.uid,
            "programs",
            "strength-for-life",
            "completions"
        );

        return onSnapshot(
            completionsRef,
            (snapshot) => {
                const nextCompletedKeys = new Set(snapshot.docs.map((docSnap) => docSnap.id));
                setCompletedProgressKeys(nextCompletedKeys);
                setCompletionLoadError("");
            },
            (error) => {
                console.error("Unable to load Strength for Life completions", error);
                setCompletedProgressKeys(new Set());
                setCompletionLoadError("Unable to load your saved progress right now.");
            }
        );
    }, [isCompletionEnabled, user?.uid]);

    useEffect(() => {
        setCompletionActionError("");
    }, [selectedExercise?.progressKey]);

    const handleSelectWorkoutDay = (workoutDay) => {
        setCompletionActionError("");
        setSelectedExercise(workoutDay);
    };

    const handleCloseOverlay = () => {
        setCompletionActionError("");
        setSelectedExercise(null);
    };

    const handleToggleCompletion = async (workoutDay) => {
        if (!isCompletionEnabled || !user?.uid || !workoutDay?.progressKey) {
            return;
        }

        const progressKey = workoutDay.progressKey;
        const wasCompleted = completedProgressKeys.has(progressKey);
        const previousCompletedKeys = new Set(completedProgressKeys);
        const nextCompletedKeys = new Set(completedProgressKeys);

        setSavingProgressKey(progressKey);
        setCompletionActionError("");

        if (wasCompleted) {
            nextCompletedKeys.delete(progressKey);
        } else {
            nextCompletedKeys.add(progressKey);
        }
        setCompletedProgressKeys(nextCompletedKeys);

        try {
            const completionRef = doc(
                db,
                "user_activities",
                user.uid,
                "programs",
                "strength-for-life",
                "completions",
                progressKey
            );

            if (wasCompleted) {
                await deleteDoc(completionRef);
            } else {
                await setDoc(completionRef, {
                    programId: workoutDay.programId,
                    progressKey,
                    trackId: workoutDay.trackId,
                    phaseId: workoutDay.phaseId,
                    weekNumber: workoutDay.weekNumber,
                    dayNumber: workoutDay.dayNumber,
                    completedAt: serverTimestamp(),
                    updatedAt: serverTimestamp(),
                });
            }
        } catch (error) {
            console.error("Unable to update Strength for Life completion", error);
            setCompletedProgressKeys(previousCompletedKeys);
            setCompletionActionError("Unable to save this workout right now. Please try again.");
        } finally {
            setSavingProgressKey("");
        }
    };

    // If no week data, show placeholder
    if (!weekData) {
        return (
            <div className="min-h-screen py-5 px-1.5 sm:px-2 md:py-8 md:px-4" style={{ background: '#ffffff' }}>
                <div className="max-w-4xl mx-auto">
                    <SubMenuWrapper>
                        <SubMenuItem
                            icon={<ArrowBackIcon size={24} color="#6b7280" />}
                            label={programData?.title || "Back"}
                            to={`/strong-healthy/${programId}`}
                        />
                    </SubMenuWrapper>
                    <div className="text-center mt-8">
                        <p style={{ color: '#64748b' }}>Week content not found.</p>
                    </div>
                </div>
            </div>
        );
    }

    const exercises = weekData.exercises || [];
    const isStructuredPlan = exercises.length > 0 && exercises.every(hasStructuredPhaseContent);

    return (
        <div className="min-h-screen py-5 px-1.5 sm:px-2 md:py-8 md:px-4" style={{ background: '#ffffff' }}>
            <div className="max-w-4xl mx-auto">
                {/* SubMenu */}
                <SubMenuWrapper>
                    <SubMenuItem
                        icon={<ArrowBackIcon size={24} color="#6b7280" />}
                        label={programData?.title || "Back to Program"}
                        to={`/strong-healthy/${programId}`}
                    />
                </SubMenuWrapper>

                {/* Week Header */}
                <div className="text-center mb-6 mt-4">
                    {programData?.badge && (
                        <span className="inline-block px-4 py-1 rounded-full text-sm font-medium mb-3" style={{
                            background: 'linear-gradient(90deg, #22c55e 0%, #16a34a 100%)',
                            color: 'white'
                        }}>
                            {programData.badge}
                        </span>
                    )}
                    {weekData.title && (
                        <h1 className="text-3xl md:text-4xl font-extrabold mb-3" style={{
                            background: 'linear-gradient(90deg, #1e293b 0%, #334155 100%)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent'
                        }}>
                            {weekData.title}
                        </h1>
                    )}
                    {weekData.description && (
                        <p className="text-base md:text-lg" style={{ color: '#64748b' }}>
                            {weekData.description}
                        </p>
                    )}
                </div>

                {completionLoadError && isCompletionEnabled && (
                    <div
                        className="rounded-2xl px-4 py-3 mb-4 text-sm"
                        style={{
                            background: "rgba(254, 242, 242, 0.92)",
                            border: "1px solid rgba(248, 113, 113, 0.2)",
                            color: "#b91c1c",
                        }}
                    >
                        {completionLoadError}
                    </div>
                )}

                {/* Exercises Grid */}
                {isStructuredPlan ? (
                    <StructuredTrackPlan
                        programId={programId}
                        track={weekData}
                        phases={exercises}
                        completedProgressKeys={completedProgressKeys}
                        onSelectWorkoutDay={handleSelectWorkoutDay}
                    />
                ) : exercises.length > 0 ? (
                    <div className="grid gap-4 md:grid-cols-2">
                        {exercises.map((exercise) => (
                            <ExerciseCard
                                key={exercise.id}
                                exercise={exercise}
                                onClick={() => setSelectedExercise(exercise)}
                            />
                        ))}
                    </div>
                ) : (
                    <div className="rounded-3xl p-6 md:p-8 text-center" style={{
                        background: 'linear-gradient(135deg, #f1f5f9 0%, #e0e7ff 100%)',
                        border: '1px solid rgba(99, 102, 241, 0.15)'
                    }}>
                        <p style={{ color: '#64748b' }}>No exercises available yet.</p>
                    </div>
                )}

                {/* Exercise Overlay */}
                {selectedExercise && (
                    <ExerciseOverlay
                        exercise={selectedExercise}
                        onClose={handleCloseOverlay}
                        canToggleComplete={isCompletionEnabled}
                        isCompleted={completedProgressKeys.has(selectedExercise.progressKey)}
                        isSavingCompletion={savingProgressKey === selectedExercise.progressKey}
                        completionError={completionActionError}
                        onToggleComplete={() => handleToggleCompletion(selectedExercise)}
                    />
                )}
            </div>
        </div>
    );
}

function StructuredTrackPlan({ programId, track, phases, onSelectWorkoutDay, completedProgressKeys }) {
    return (
        <div className="grid gap-5 md:gap-6">
            <div className="flex flex-wrap gap-2">
                {phases.map((phase) => (
                    <a
                        key={phase.id}
                        href={`#${phase.id}`}
                        className="no-underline px-2.5 py-1 rounded-full text-xs md:px-3 md:py-1.5 md:text-sm font-semibold transition-all duration-200 hover:scale-[1.02]"
                        style={{
                            background: "rgba(99, 102, 241, 0.08)",
                            color: "#334155",
                        }}
                    >
                        {phase.title}
                    </a>
                ))}
            </div>

            {phases.map((phase) => {
                const phaseProgress = getPhaseProgress(programId, track, phase, completedProgressKeys);

                return (
                    <section
                        key={phase.id}
                        id={phase.id}
                        className="rounded-3xl p-3 sm:p-4 md:p-6"
                        style={{
                            background: "linear-gradient(135deg, #f8fafc 0%, #eef4ff 100%)",
                            border: "1px solid rgba(99, 102, 241, 0.14)",
                        }}
                    >
                        <div className="mb-5">
                            <h2 className="text-2xl md:text-3xl font-bold mb-2" style={{ color: "#1e293b" }}>
                                {phase.title}
                            </h2>
                            {phase.description && (
                                <p className="text-sm md:text-base m-0" style={{ color: "#64748b", lineHeight: 1.7 }}>
                                    {phase.description}
                                </p>
                            )}

                            <div
                                className="rounded-2xl px-3 py-3 sm:px-4 mt-4"
                                style={{
                                    background: "rgba(255, 255, 255, 0.82)",
                                    border: "1px solid rgba(148, 163, 184, 0.16)",
                                }}
                            >
                                <div className="flex items-center justify-between gap-3 mb-2">
                                    <span className="text-sm font-semibold" style={{ color: "#1e293b" }}>
                                        Phase progress
                                    </span>
                                    <span className="text-sm font-semibold" style={{ color: "#334155" }}>
                                        {phaseProgress.completedDays}/{phaseProgress.totalDays} completed
                                    </span>
                                </div>

                                <div
                                    className="w-full h-2 rounded-full overflow-hidden"
                                    style={{ background: "rgba(148, 163, 184, 0.18)" }}
                                >
                                    <div
                                        className="h-full rounded-full transition-all duration-300"
                                        style={{
                                            width: `${phaseProgress.percentComplete}%`,
                                            background: phaseProgress.completedDays > 0
                                                ? "linear-gradient(90deg, #16a34a 0%, #22c55e 100%)"
                                                : "linear-gradient(90deg, #cbd5e1 0%, #e2e8f0 100%)",
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>

                        <PhaseScheduleGrid
                            programId={programId}
                            track={track}
                            phase={phase}
                            completedProgressKeys={completedProgressKeys}
                            onSelectWorkoutDay={onSelectWorkoutDay}
                        />
                    </section>
                );
            })}
        </div>
    );
}

function PhaseScheduleGrid({ programId, track, phase, onSelectWorkoutDay, completedProgressKeys }) {
    const phaseWeeks = getPhaseWeeks(phase);
    const maxDayCount = phaseWeeks.reduce(
        (largestCount, week) => Math.max(largestCount, week.sections?.length || 0),
        0
    );

    return (
        <div className="grid gap-3 md:gap-4">
            <p className="text-sm md:text-base m-0" style={{ color: "#64748b" }}>
                Follow this phase week by week. Click any day to open that workout's details.
            </p>

            <div className="md:hidden grid gap-2.5">
                {phaseWeeks.map((week) => (
                    <div
                        key={week.id || `${phase.id}-week-${week.weekNumber}`}
                        className="rounded-3xl p-3 sm:p-4"
                        style={{
                            background: "rgba(255, 255, 255, 0.92)",
                            border: "1px solid rgba(148, 163, 184, 0.18)",
                        }}
                    >
                        <div className="mb-2">
                            <WeekLabel weekNumber={week.weekNumber} />
                        </div>

                        <div className="grid gap-1.5 sm:gap-2">
                            {week.sections?.map((section, dayIndex) => {
                                const workoutDay = buildWorkoutDay(programId, track, phase, week, section, dayIndex);
                                return (
                                    <WorkoutScheduleButton
                                        key={`${phase.id}-week-${week.weekNumber}-day-${dayIndex + 1}`}
                                        label={`Day ${dayIndex + 1}`}
                                        summary={getSectionSummary(section, dayIndex)}
                                        isCompleted={completedProgressKeys.has(workoutDay.progressKey)}
                                        onClick={() => onSelectWorkoutDay(workoutDay)}
                                    />
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>

            <div className="hidden md:block overflow-x-auto">
                <table
                    aria-label={`${phase.title} weekly workout schedule`}
                    className="w-full min-w-[760px] border-separate border-spacing-0 overflow-hidden rounded-3xl"
                >
                    <tbody>
                        {phaseWeeks.map((week, rowIndex) => (
                            <tr
                                key={week.id || `${phase.id}-week-${week.weekNumber}`}
                                style={{
                                    background: rowIndex % 2 === 0 ? "rgba(255,255,255,0.94)" : "rgba(248,250,252,0.94)",
                                }}
                            >
                                <ScheduleWeekCell>
                                    <WeekLabel weekNumber={week.weekNumber} />
                                </ScheduleWeekCell>
                                {Array.from({ length: maxDayCount }, (_, dayIndex) => {
                                    const section = week.sections?.[dayIndex];

                                    if (!section) {
                                        return <ScheduleCell key={`${phase.id}-week-${week.weekNumber}-day-${dayIndex + 1}`} />;
                                    }

                                    const workoutDay = buildWorkoutDay(programId, track, phase, week, section, dayIndex);
                                    return (
                                        <ScheduleCell key={`${phase.id}-week-${week.weekNumber}-day-${dayIndex + 1}`}>
                                            <WorkoutScheduleButton
                                                label={`Day ${dayIndex + 1}`}
                                                summary={getSectionSummary(section, dayIndex)}
                                                isCompleted={completedProgressKeys.has(workoutDay.progressKey)}
                                                onClick={() => onSelectWorkoutDay(workoutDay)}
                                            />
                                        </ScheduleCell>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function hasStructuredPhaseContent(phase) {
    if (Array.isArray(phase.sections) && phase.sections.length > 0) {
        return true;
    }

    return Array.isArray(phase.weeks)
        && phase.weeks.length > 0
        && phase.weeks.every((week) => Array.isArray(week.sections) && week.sections.length > 0);
}

function WorkoutScheduleButton({ label, summary, onClick, isCompleted = false }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className="w-full text-left rounded-2xl p-2.5 sm:p-3 md:p-4 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg"
            style={{
                background: isCompleted
                    ? "linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)"
                    : "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
                border: isCompleted
                    ? "1px solid rgba(34, 197, 94, 0.24)"
                    : "1px solid rgba(148, 163, 184, 0.16)",
            }}
        >
            <div className="flex items-start justify-between gap-2 mb-1">
                <span className="block text-sm font-bold" style={{ color: "#1e293b" }}>
                    {label}
                </span>
                {isCompleted && (
                    <span
                        className="shrink-0 inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold"
                        style={{
                            background: "rgba(34, 197, 94, 0.14)",
                            color: "#166534",
                        }}
                    >
                        Completed
                    </span>
                )}
            </div>
            <span
                className="block text-sm"
                style={{ color: isCompleted ? "#166534" : "#475569", lineHeight: 1.5 }}
            >
                {summary}
            </span>
        </button>
    );
}

function ScheduleWeekCell({ children }) {
    return (
        <td
            className="align-top px-4 py-4 text-sm font-bold whitespace-nowrap"
            style={{ color: "#1e293b", borderTop: "1px solid rgba(148, 163, 184, 0.16)" }}
        >
            {children}
        </td>
    );
}

function WeekLabel({ weekNumber }) {
    const accent = getWeekAccent(weekNumber);

    return (
        <div className="inline-flex flex-col items-start gap-1.5">
            <span className="text-base md:text-lg font-bold" style={{ color: "#1e293b" }}>
                Week {weekNumber}
            </span>
            <span
                aria-hidden="true"
                className="block h-1 rounded-full"
                style={{
                    width: "3rem",
                    background: accent.gradient,
                    boxShadow: accent.shadow,
                }}
            />
        </div>
    );
}

function ScheduleCell({ children }) {
    return (
        <td
            className="align-top p-3"
            style={{ borderTop: "1px solid rgba(148, 163, 184, 0.16)" }}
        >
            {children}
        </td>
    );
}

function getWeekNumbers(phase) {
    if (Array.isArray(phase.weeks) && phase.weeks.length > 0) {
        return phase.weeks.map((week) => week.weekNumber);
    }

    const match = phase.title?.match(/Weeks?\s+(\d+)\D+(\d+)/i);

    if (!match) {
        return [1, 2, 3, 4];
    }

    const start = Number(match[1]);
    const end = Number(match[2]);

    return Array.from({ length: end - start + 1 }, (_, index) => start + index);
}

function getSectionSummary(section, dayIndex) {
    const summary = section.title?.split("—")[1]?.trim();
    return summary || `Workout ${dayIndex + 1}`;
}

function getWeekAccent(weekNumber) {
    const accents = [
        {
            gradient: "linear-gradient(90deg, #22c55e 0%, #14b8a6 100%)",
            shadow: "0 4px 14px rgba(20, 184, 166, 0.18)",
        },
        {
            gradient: "linear-gradient(90deg, #0ea5e9 0%, #6366f1 100%)",
            shadow: "0 4px 14px rgba(99, 102, 241, 0.18)",
        },
        {
            gradient: "linear-gradient(90deg, #f59e0b 0%, #f97316 100%)",
            shadow: "0 4px 14px rgba(249, 115, 22, 0.18)",
        },
        {
            gradient: "linear-gradient(90deg, #ec4899 0%, #ef4444 100%)",
            shadow: "0 4px 14px rgba(239, 68, 68, 0.18)",
        },
    ];

    return accents[(Math.max(weekNumber, 1) - 1) % accents.length];
}

function getPhaseProgress(programId, track, phase, completedProgressKeys) {
    const phaseWeeks = getPhaseWeeks(phase);
    const totalDays = phaseWeeks.reduce(
        (sum, week) => sum + (week.sections?.length || 0),
        0
    );

    let completedDays = 0;

    phaseWeeks.forEach((week) => {
        week.sections?.forEach((section, dayIndex) => {
            const workoutDay = buildWorkoutDay(programId, track, phase, week, section, dayIndex);
            if (completedProgressKeys.has(workoutDay.progressKey)) {
                completedDays += 1;
            }
        });
    });

    const percentComplete = totalDays > 0 ? Math.round((completedDays / totalDays) * 100) : 0;

    return {
        totalDays,
        completedDays,
        remainingDays: Math.max(0, totalDays - completedDays),
        percentComplete,
    };
}

function getPhaseWeeks(phase) {
    if (Array.isArray(phase.weeks) && phase.weeks.length > 0) {
        return phase.weeks;
    }

    return getWeekNumbers(phase).map((weekNumber) => ({
        id: `${phase.id}-week-${weekNumber}`,
        weekNumber,
        title: `Week ${weekNumber}`,
        sections: phase.sections || [],
    }));
}

function buildWorkoutDay(programId, track, phase, week, section, dayIndex) {
    const dayNumber = dayIndex + 1;
    const progressKey = `${track.id}:${phase.id}:week-${week.weekNumber}:day-${dayNumber}`;

    return {
        id: progressKey,
        title: `${track.title} · Week ${week.weekNumber} · Day ${dayNumber}`,
        programId,
        trackId: track.id,
        phaseId: phase.id,
        weekNumber: week.weekNumber,
        dayNumber,
        sections: [
            {
                id: `${progressKey}:section`,
                hideTitle: true,
                items: section.items,
            },
        ],
        progressKey,
    };
}

/**
 * ExerciseCard - Clickable card for individual exercises
 */
function ExerciseCard({ exercise, onClick }) {
    // Determine what icon to show based on content
    const hasVideo = !!exercise.vimeoId;
    const hasLink = !!exercise.link;
    const hasSections = Array.isArray(exercise.sections) && exercise.sections.length > 0;

    let icon = "📋"; // Default
    if (hasVideo) icon = "🎬";
    else if (hasLink) icon = "📎";
    else if (hasSections) icon = "🗂️";

    return (
        <div
            onClick={onClick}
            className="rounded-2xl p-5 md:p-6 transition-all duration-300 hover:scale-[1.02] hover:shadow-xl cursor-pointer"
            style={{
                background: 'linear-gradient(135deg, #f1f5f9 0%, #e0e7ff 100%)',
                border: '1px solid rgba(99, 102, 241, 0.15)'
            }}
        >
            <div className="flex items-start gap-4">
                <span className="text-3xl">{icon}</span>
                <div className="flex-1">
                    {exercise.title && (
                        <h3 className="text-lg font-bold mb-1" style={{ color: '#1e293b' }}>
                            {exercise.title}
                        </h3>
                    )}
                    {exercise.description && (
                        <p className="text-sm m-0 line-clamp-2" style={{ color: '#64748b' }}>
                            {exercise.description}
                        </p>
                    )}

                    {/* Content indicators */}
                    <div className="flex gap-2 mt-3">
                        {hasSections && (
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium" style={{
                                background: 'rgba(14, 165, 233, 0.1)',
                                color: '#0369a1'
                            }}>
                                Plan Details
                            </span>
                        )}
                        {hasVideo && (
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium" style={{
                                background: 'rgba(99, 102, 241, 0.1)',
                                color: '#6366f1'
                            }}>
                                Video
                            </span>
                        )}
                        {hasLink && (
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium" style={{
                                background: 'rgba(34, 197, 94, 0.1)',
                                color: '#16a34a'
                            }}>
                                Resource
                            </span>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
